import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import AuthLayout from '../components/AuthLayout';
import { verifyPhoneLinkToken } from '../services/authService';

const Title = styled.h1`
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  margin-bottom: 30px;
  color: #555;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  line-height: 1.5;
`;

const Message = styled.div`
  margin: 20px 0;
  padding: 15px;
  border-radius: 8px;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
`;

const SuccessMessage = styled(Message)`
  background-color: #e8f5e9;
  color: #2e7d32;
`;

const ErrorMessage = styled(Message)`
  background-color: #ffebee;
  color: #c62828;
`;

const LoadingMessage = styled(Message)`
  background-color: #e3f2fd;
  color: #1565c0;
`;

const VerifyPhoneByLink: React.FC = () => {
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyPhone = async () => {
      // Extract the token from URL search params
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');
      
      if (!token) {
        setError('Invalid or missing verification token.');
        setIsVerifying(false);
        return;
      }
      
      try {
        console.log('Verifying phone with token:', token);
        
        // Keep the token as-is without decoding (the server will handle decoding if needed)
        // Just trim whitespace from ends
        const cleanToken = token.trim();
        console.log('Clean token being sent:', cleanToken);
        
        // Add retry logic with a short delay
        let retryCount = 0;
        const maxRetries = 2;
        
        while (retryCount <= maxRetries) {
          try {
            const result = await verifyPhoneLinkToken({ token: cleanToken });
            console.log('Verification result:', result);
            setIsSuccess(true);
            setIsVerifying(false);
            
            // Redirect to dashboard after successful verification
            setTimeout(() => {
              navigate('/dashboard');
            }, 3000);
            
            return; // Exit on success
          } catch (retryError: any) {
            console.error(`Verification attempt ${retryCount + 1} failed:`, retryError);
            
            if (retryCount < maxRetries) {
              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, 1000));
              retryCount++;
            } else {
              // All retries failed
              throw retryError;
            }
          }
        }
      } catch (error: any) {
        console.error('Phone verification error:', error);
        
        // Provide more descriptive errors based on error message
        if (error.message?.includes('expired')) {
          setError('Your verification link has expired. Please request a new verification link from your profile.');
        } else if (error.message?.includes('Invalid token')) {
          setError('Invalid verification token. Please request a new verification link from your profile page.');
        } else if (error.message?.includes('User not found')) {
          setError('User account not found. Please contact support if this issue persists.');
        } else if (error.message?.includes('signature')) {
          setError('This verification link appears to be invalid. Please request a new verification link from your profile.');
        } else {
          setError(error.message || 'Failed to verify phone number. Please try again from your profile page.');
        }
        
        setIsVerifying(false);
      }
    };

    verifyPhone();
  }, [location.search, navigate]);

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Phone Verification</title>
        <meta name="description" content={`${APP_NAME} - Phone Verification`} />
      </Helmet>
      <AuthLayout imageUrl="/assets/otp-image.jpg" altText="Phone Verification">
        <Title>Phone Verification</Title>
        
        {isVerifying ? (
          <>
            <Subtitle>Verifying your phone number...</Subtitle>
            <LoadingMessage>Please wait while we verify your phone number.</LoadingMessage>
          </>
        ) : isSuccess ? (
          <>
            <Subtitle>Verification Successful</Subtitle>
            <SuccessMessage>
              <p>Your phone number has been successfully verified!</p>
              <p>You'll be redirected to your dashboard in a moment...</p>
            </SuccessMessage>
          </>
        ) : (
          <>
            <Subtitle>Phone Verification Failed</Subtitle>
            <ErrorMessage>
              <p>{error || 'An unknown error occurred during verification.'}</p>
              <p>You can try again from your profile page or go to your dashboard.</p>
              <div style={{ marginTop: '20px' }}>
                <button 
                  onClick={() => navigate('/dashboard')}
                  style={{ 
                    padding: '8px 16px', 
                    backgroundColor: '#3b1b79', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    marginRight: '10px',
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                >
                  Go to Dashboard
                </button>
                <button 
                  onClick={() => window.location.reload()}
                  style={{ 
                    padding: '8px 16px', 
                    backgroundColor: '#1976d2', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                >
                  Retry Verification
                </button>
              </div>
            </ErrorMessage>
          </>
        )}
      </AuthLayout>
    </>
  );
};

export default VerifyPhoneByLink;
